// @ts-nocheck
/* Port of components/BreakStatusCapsule.js (167 lines) — byte-identical.
 * Renders a countdown capsule/hover overlay for agents on a break; uses
 * breakStatus utility to compute remaining/exceeded state. */
import React, { useEffect, useRef, useState } from 'react';
import { FaClock, FaExclamationTriangle } from 'react-icons/fa';
import {
  computeBreakCountdown,
  formatBreakTime,
  getBreakCapsuleColor,
  resolveBreakFromRow,
} from '@/utilities/breakStatus';

const BreakStatusCapsule = ({ agent, user, activeUserBreak, currentTime }: any) => {
  const [isDarkMode, setIsDarkMode] = useState(
    typeof document !== 'undefined' && document.body.classList.contains('dark'),
  );
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const body = document.body;
    const updateTheme = () => setIsDarkMode(body.classList.contains('dark'));
    const observer = new MutationObserver(updateTheme);
    updateTheme();
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const positionOverlay = () => {
    const el = iconRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, left: r.left + r.width / 2 });
  };

  const showOverlay = () => {
    positionOverlay();
    setOpen(true);
  };
  const hideOverlay = () => setOpen(false);
  const toggleOverlay = () => {
    if (open) setOpen(false);
    else showOverlay();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const breakInfo = resolveBreakFromRow({ agent, user, activeUserBreak });
  if (!breakInfo) {
    return <span style={{ opacity: 0.5, color: isDarkMode ? '#94a3b8' : undefined }}>-</span>;
  }

  const countdown = computeBreakCountdown(breakInfo, currentTime);
  const totalSeconds = breakInfo.durationMinutes * 60;
  const exceeded = countdown !== null && countdown <= 0;
  const color = exceeded ? '#dc3545' : getBreakCapsuleColor(countdown, totalSeconds);

  const overlayBg = isDarkMode ? '#1e293b' : '#ffffff';
  const overlayBorder = isDarkMode ? '#334155' : '#e2e8f0';
  const overlayText = isDarkMode ? '#e2e8f0' : '#1f2937';

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        onMouseEnter={showOverlay}
        onMouseLeave={hideOverlay}
        onFocus={showOverlay}
        onBlur={hideOverlay}
        onClick={toggleOverlay}
        title={exceeded ? 'Break Time Exceeded' : 'Break Remaining'}
        aria-label={exceeded ? 'Break time exceeded' : 'On break'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: '50%',
          border: `1px solid ${color}`,
          backgroundColor: color,
          color: '#ffffff',
          cursor: 'pointer',
          padding: 0,
          boxShadow: exceeded ? `0 0 0 3px ${isDarkMode ? 'rgba(239,68,68,0.25)' : 'rgba(220,53,69,0.2)'}` : 'none',
        }}
      >
        {exceeded ? <FaExclamationTriangle size={13} /> : <FaClock size={13} />}
      </button>

      {open && (
        <div
          onMouseEnter={showOverlay}
          onMouseLeave={hideOverlay}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%)',
            zIndex: 2000,
            minWidth: 180,
            maxWidth: 240,
            backgroundColor: overlayBg,
            border: `1px solid ${overlayBorder}`,
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: '10px 12px',
            fontSize: 12,
            color: overlayText,
            textAlign: 'left',
          }}
        >
          <div className="d-flex align-items-center gap-2" style={{ marginBottom: 6, fontWeight: 700 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: color,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {exceeded ? <FaExclamationTriangle size={11} /> : <FaClock size={11} />}
            </span>
            <span>{breakInfo.title}</span>
          </div>

          {breakInfo.durationMinutes > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ opacity: 0.7 }}>Duration</span>
              <span style={{ fontWeight: 600 }}>{breakInfo.durationMinutes}m</span>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ opacity: 0.7 }}>{exceeded ? 'Status' : 'Remaining'}</span>
            <span style={{ fontWeight: 700, color: exceeded ? '#ef4444' : undefined }}>
              {exceeded ? 'Break Exceeded' : formatBreakTime(countdown)}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default BreakStatusCapsule;
